"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import {
  cinematicModalPrimaryButtonClass,
  cinematicModalSecondaryButtonClass,
} from "@/lib/cinematicModalStyles";
import { createLogger } from "@/lib/logger";
import { Copy, Heart, Loader2, Play, Plus, Volume2 } from "lucide-react";
import Link from "next/link";
import * as React from "react";

const logger = createLogger("flashcard-tooltip");

export interface FlashcardData {
  ipaSpelling: Record<string, string>;
  translation: string;
  exampleSentences: Array<{
    sentence: string;
    translation: string;
  }>;
  grammarPoints: Array<{
    title: string;
    explanation: string;
  }>;
  vocabulary: Array<{
    word: string;
    partOfSpeech: string;
    definition: string;
    usage?: string;
  }>;
}

interface FlashcardTooltipProps {
  sentence: string;
  position: { x: number; y: number };
  onClose: () => void;
  targetLanguage: string;
  instructionLanguage: string;
  languageLevel?: string;
  userId?: string;
  onSave?: (sentence: string, data: FlashcardData) => Promise<void>;
  onFavorite?: (sentence: string) => Promise<void>;
  isFavorited?: boolean;
  isSaved?: boolean;
}

export function FlashcardTooltip({
  sentence,
  onClose,
  targetLanguage,
  instructionLanguage,
  languageLevel = "intermediate",
  userId,
  onSave,
  onFavorite,
  isFavorited = false,
  isSaved = false,
}: FlashcardTooltipProps) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<FlashcardData | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [favoriting, setFavoriting] = React.useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = React.useState(false);
  // Fetch flashcard data from API
  React.useEffect(() => {
    const fetchFlashcardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch("/api/generate-flashcard", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sentence,
            targetLanguage,
            instructionLanguage,
            languageLevel,
            userId,
          }),
        });

        if (!response.ok) {
          const errorData = (await response.json()) as any;
          throw new Error(errorData.error || "Failed to generate flashcard");
        }

        const result = (await response.json()) as any;
        setData(result.data);
      } catch {
        setError("We couldn't load the language details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlashcardData();
  }, [sentence, targetLanguage, instructionLanguage, languageLevel, userId]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sentence);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error("Failed to copy", { error: err instanceof Error ? err.message : String(err) });
    }
  };

  // Handle text-to-speech
  const handleSpeak = () => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(sentence);

      // Try to set language based on targetLanguage
      const langCode = targetLanguage.toLowerCase().includes("spanish")
        ? "es-ES"
        : targetLanguage.toLowerCase().includes("french")
          ? "fr-FR"
          : targetLanguage.toLowerCase().includes("german")
            ? "de-DE"
            : targetLanguage.toLowerCase().includes("italian")
              ? "it-IT"
              : targetLanguage.toLowerCase().includes("dutch")
                ? "nl-NL"
                : targetLanguage.toLowerCase().includes("portuguese")
                  ? "pt-PT"
                  : targetLanguage.toLowerCase().includes("russian")
                    ? "ru-RU"
                    : targetLanguage.toLowerCase().includes("japanese")
                      ? "ja-JP"
                      : targetLanguage.toLowerCase().includes("chinese")
                        ? "zh-CN"
                        : "en-US";

      utterance.lang = langCode;
      speechSynthesis.speak(utterance);
    }
  };

  // Handle save flashcard
  const handleSave = async () => {
    // Show upgrade prompt for unauthenticated users
    if (!userId) {
      setShowUpgradePrompt(true);
      return;
    }

    if (!onSave || !data || saving) return;

    try {
      setSaving(true);
      await onSave(sentence, data);
    } catch (err) {
      logger.error("Failed to save flashcard", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle favorite toggle
  const handleFavorite = async () => {
    if (!onFavorite || favoriting) return;

    try {
      setFavoriting(true);
      await onFavorite(sentence);
    } catch (err) {
      logger.error("Failed to toggle favorite", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setFavoriting(false);
    }
  };

  return (
    <CinematicModal
      isOpen
      onClose={onClose}
      ariaLabel="Flashcard details"
      maxWidth="lg"
      closeButtonClassName="h-11 w-11"
      contentClassName="p-0 sm:p-0"
    >
      <div className="text-white">
        {/* Header with sentence and action buttons */}
        <div className="px-5 py-4 border-b border-white/10">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0 flex-1 break-words pr-12 text-base font-medium text-white leading-relaxed">
              {sentence}
            </div>
          </div>

          {/* Action buttons row */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleSpeak}
              className={cinematicModalSecondaryButtonClass}
              aria-label="Play pronunciation"
            >
              <Volume2 size={16} />
              <span>Play</span>
            </button>

            <button
              onClick={handleCopy}
              className={cinematicModalSecondaryButtonClass}
              aria-label="Copy word"
            >
              <Copy size={16} />
              <span>{copied ? "Copied!" : "Copy"}</span>
            </button>

            {onFavorite && (
              <button
                onClick={handleFavorite}
                disabled={favoriting}
                className={`flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60 ${
                  isFavorited
                    ? "bg-[#FD954F]/10 text-[#FD954F] hover:bg-[#FD954F]/15"
                    : "bg-white/[0.04] hover:bg-white/[0.07] text-white/70"
                }`}
                aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
              >
                <Heart size={16} className={isFavorited ? "fill-current" : ""} />
              </button>
            )}

            {onSave && !isSaved && (
              <button
                onClick={handleSave}
                disabled={saving || !data}
                className={`${cinematicModalPrimaryButtonClass} ml-auto`}
                aria-label="Save flashcard"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                <span>{saving ? "Saving..." : "Save"}</span>
              </button>
            )}

            {isSaved && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium ml-auto">
                <span>✓ Saved</span>
              </div>
            )}
          </div>
        </div>

        {showUpgradePrompt && (
          <div className="border-b border-white/10 px-5 py-4">
            <h3 className="text-base font-semibold text-white">Save your flashcards</h3>
            <p className="mt-1 text-sm text-white/55">
              Create a free account to save flashcards to your library and track your progress.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link href="/auth" className={cinematicModalPrimaryButtonClass}>
                Sign up free
              </Link>
              <button
                type="button"
                onClick={() => setShowUpgradePrompt(false)}
                className="min-h-11 px-3 text-sm text-white/55 hover:text-white"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}

        {/* Content area */}
        <div className="px-5 py-4 break-words">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          )}

          {error && (
            <div className="text-center py-6">
              <div className="text-red-400 mb-2">Failed to load flashcard</div>
              <div className="text-sm text-gray-400">{error}</div>
            </div>
          )}

          {data && (
            <div className="space-y-4">
              {/* IPA Pronunciation */}
              {Object.keys(data.ipaSpelling).length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-white/55 mb-2">
                    Pronunciation
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(data.ipaSpelling).map(([word, ipa]) => (
                      <div key={word} className="inline-flex items-center gap-2 py-1.5">
                        <span className="text-sm text-gray-200 font-medium">{word}</span>
                        <span className="text-xs text-white/70 font-mono">[{ipa}]</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Translation */}
              {data.translation && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-white/55 mb-2">
                    Translation
                  </h3>
                  <p className="text-sm text-gray-200 leading-relaxed italic">{data.translation}</p>
                </div>
              )}

              {/* Vocabulary */}
              {data.vocabulary && data.vocabulary.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-white/55 mb-2">
                    Vocabulary
                  </h3>
                  <div className="space-y-2">
                    {data.vocabulary.map((vocab, idx) => (
                      <div key={idx} className="border-t border-white/10 pt-3">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="text-sm font-semibold text-white">{vocab.word}</span>
                          <span className="text-xs text-gray-500 italic">
                            ({vocab.partOfSpeech})
                          </span>
                        </div>
                        <p className="text-sm text-gray-300">{vocab.definition}</p>
                        {vocab.usage && (
                          <p className="text-xs text-gray-400 mt-1 italic">{vocab.usage}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Grammar Points */}
              {data.grammarPoints && data.grammarPoints.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-white/55 mb-2">
                    Grammar
                  </h3>
                  <div className="space-y-2">
                    {data.grammarPoints.map((point, idx) => (
                      <div key={idx} className="border-t border-white/10 pt-3">
                        <div className="text-sm font-semibold text-white mb-1">{point.title}</div>
                        <p className="text-sm text-gray-300 leading-relaxed">{point.explanation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Example Sentences */}
              {data.exampleSentences && data.exampleSentences.length > 0 && (
                <div>
                  <h3 className="text-xs uppercase tracking-wide font-semibold text-white/55 mb-2">
                    Examples
                  </h3>
                  <div className="space-y-2">
                    {data.exampleSentences.map((example, idx) => (
                      <div key={idx} className="border-t border-white/10 pt-3">
                        <p className="text-sm text-gray-200 mb-1">{example.sentence}</p>
                        <p className="text-xs text-gray-400 italic">{example.translation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CinematicModal>
  );
}
